package com.fantasytenis.web.hooks

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import kotlin.math.abs
import kotlin.math.sqrt

/**
 * Touch gesture types that can be detected.
 */
sealed class TouchGesture {
    /** Single tap on an element */
    data class Tap(val x: Double, val y: Double) : TouchGesture()

    /** Double tap on an element */
    data class DoubleTap(val x: Double, val y: Double) : TouchGesture()

    /** Long press on an element */
    data class LongPress(val x: Double, val y: Double) : TouchGesture()

    /** Swipe in a direction */
    data class Swipe(val direction: SwipeDirection, val velocity: Double) : TouchGesture()

    /** Pinch to zoom */
    data class Pinch(val scale: Double, val centerX: Double, val centerY: Double) : TouchGesture()

    /** Pan/drag gesture */
    data class Pan(val deltaX: Double, val deltaY: Double, val isActive: Boolean) : TouchGesture()
}

/**
 * Swipe directions.
 */
enum class SwipeDirection {
    LEFT, RIGHT, UP, DOWN
}

/**
 * State for tracking touch gestures.
 */
data class TouchState(
    val isPanning: Boolean = false,
    val panDeltaX: Double = 0.0,
    val panDeltaY: Double = 0.0,
    val zoomScale: Double = 1.0,
    val zoomCenterX: Double = 0.0,
    val zoomCenterY: Double = 0.0
)

/**
 * Configuration for touch gesture detection.
 */
data class TouchGestureConfig(
    /** Minimum distance for swipe detection */
    val swipeThreshold: Double = 50.0,
    /** Minimum velocity for swipe detection (px/ms) */
    val swipeVelocityThreshold: Double = 0.3,
    /** Time threshold for long press (ms) */
    val longPressThreshold: Long = 500L,
    /** Time window for double tap (ms) */
    val doubleTapWindow: Long = 300L,
    /** Maximum movement allowed during tap */
    val tapMovementThreshold: Double = 10.0
)

/**
 * Hook for handling touch gestures on mobile devices.
 *
 * Detects:
 * - Tap / Double tap
 * - Long press
 * - Swipe (left, right, up, down)
 * - Pinch to zoom
 * - Pan/drag
 *
 * @param onGesture Callback when a gesture is detected
 * @param config Gesture detection configuration
 * @return Touch event handlers and current touch state
 */
@Composable
fun useTouchGestures(
    onGesture: (TouchGesture) -> Unit,
    config: TouchGestureConfig = TouchGestureConfig()
): TouchGestureHandlers {
    var touchState by remember { mutableStateOf(TouchState()) }

    // Track touch points
    var touchStartTime by remember { mutableStateOf(0L) }
    var touchStartX by remember { mutableStateOf(0.0) }
    var touchStartY by remember { mutableStateOf(0.0) }
    var lastTapTime by remember { mutableStateOf(0L) }
    var lastTapX by remember { mutableStateOf(0.0) }
    var lastTapY by remember { mutableStateOf(0.0) }
    var initialPinchDistance by remember { mutableStateOf(0.0) }
    var longPressTimeoutId by remember { mutableStateOf<Int?>(null) }

    // Cleanup on unmount
    DisposableEffect(Unit) {
        onDispose {
            longPressTimeoutId?.let { window.clearTimeout(it) }
        }
    }

    val handleTouchStart: (Event) -> Unit = { event ->
        val touchEvent = event.unsafeCast<TouchEvent>()
        val touches = touchEvent.touches
        val now = currentTimeMillisTouch()

        // Cancel any pending long press
        longPressTimeoutId?.let { window.clearTimeout(it) }

        if (touches.length == 1) {
            // Single touch - potential tap, long press, or pan
            val touch = touches.item(0)!!
            touchStartTime = now
            touchStartX = touch.clientX.toDouble()
            touchStartY = touch.clientY.toDouble()

            // Set up long press timer
            longPressTimeoutId = window.setTimeout({
                onGesture(TouchGesture.LongPress(touchStartX, touchStartY))
            }, config.longPressThreshold.toInt())

        } else if (touches.length == 2) {
            // Two touches - potential pinch
            val touch1 = touches.item(0)!!
            val touch2 = touches.item(1)!!
            initialPinchDistance = calculateDistance(
                touch1.clientX.toDouble(), touch1.clientY.toDouble(),
                touch2.clientX.toDouble(), touch2.clientY.toDouble()
            )
        }
    }

    val handleTouchMove: (Event) -> Unit = { event ->
        val touchEvent = event.unsafeCast<TouchEvent>()
        val touches = touchEvent.touches

        // Cancel long press if moving
        longPressTimeoutId?.let {
            window.clearTimeout(it)
            longPressTimeoutId = null
        }

        if (touches.length == 1) {
            // Pan gesture
            val touch = touches.item(0)!!
            val currentX = touch.clientX.toDouble()
            val currentY = touch.clientY.toDouble()
            val deltaX = currentX - touchStartX
            val deltaY = currentY - touchStartY

            // Check if we've moved enough to be panning
            if (abs(deltaX) > config.tapMovementThreshold || abs(deltaY) > config.tapMovementThreshold) {
                touchState = touchState.copy(
                    isPanning = true,
                    panDeltaX = deltaX,
                    panDeltaY = deltaY
                )
                onGesture(TouchGesture.Pan(deltaX, deltaY, true))
            }

        } else if (touches.length == 2) {
            // Pinch gesture
            val touch1 = touches.item(0)!!
            val touch2 = touches.item(1)!!
            val currentDistance = calculateDistance(
                touch1.clientX.toDouble(), touch1.clientY.toDouble(),
                touch2.clientX.toDouble(), touch2.clientY.toDouble()
            )

            if (initialPinchDistance > 0) {
                val scale = currentDistance / initialPinchDistance
                val centerX = (touch1.clientX + touch2.clientX) / 2.0
                val centerY = (touch1.clientY + touch2.clientY) / 2.0

                touchState = touchState.copy(
                    zoomScale = scale,
                    zoomCenterX = centerX,
                    zoomCenterY = centerY
                )

                onGesture(TouchGesture.Pinch(scale, centerX, centerY))
            }
        }
    }

    val handleTouchEnd: (Event) -> Unit = { event ->
        val touchEvent = event.unsafeCast<TouchEvent>()
        val changedTouches = touchEvent.changedTouches
        val now = currentTimeMillisTouch()

        // Cancel long press
        longPressTimeoutId?.let {
            window.clearTimeout(it)
            longPressTimeoutId = null
        }

        if (changedTouches.length >= 1) {
            val touch = changedTouches.item(0)!!
            val endX = touch.clientX.toDouble()
            val endY = touch.clientY.toDouble()
            val deltaX = endX - touchStartX
            val deltaY = endY - touchStartY
            val distance = calculateDistance(touchStartX, touchStartY, endX, endY)
            val duration = now - touchStartTime
            val velocity = if (duration > 0) distance / duration else 0.0

            when {
                // Check for swipe
                distance >= config.swipeThreshold && velocity >= config.swipeVelocityThreshold -> {
                    val direction = when {
                        abs(deltaX) > abs(deltaY) -> {
                            if (deltaX > 0) SwipeDirection.RIGHT else SwipeDirection.LEFT
                        }
                        else -> {
                            if (deltaY > 0) SwipeDirection.DOWN else SwipeDirection.UP
                        }
                    }
                    onGesture(TouchGesture.Swipe(direction, velocity))
                }

                // Check for tap/double tap
                distance < config.tapMovementThreshold -> {
                    val timeSinceLastTap = now - lastTapTime
                    val distanceFromLastTap = calculateDistance(touchStartX, touchStartY, lastTapX, lastTapY)

                    if (timeSinceLastTap < config.doubleTapWindow && distanceFromLastTap < config.tapMovementThreshold * 2) {
                        // Double tap
                        onGesture(TouchGesture.DoubleTap(endX, endY))
                        lastTapTime = 0L // Reset to prevent triple tap
                    } else {
                        // Single tap (with delay to check for double tap)
                        lastTapTime = now
                        lastTapX = endX
                        lastTapY = endY

                        // Dispatch tap immediately - don't wait for potential double tap
                        // since we want responsive UI
                        onGesture(TouchGesture.Tap(endX, endY))
                    }
                }
            }

            // End panning
            if (touchState.isPanning) {
                onGesture(TouchGesture.Pan(deltaX, deltaY, false))
                touchState = touchState.copy(
                    isPanning = false,
                    panDeltaX = 0.0,
                    panDeltaY = 0.0
                )
            }
        }

        // Reset pinch
        if (touchEvent.touches.length == 0) {
            initialPinchDistance = 0.0
            touchState = touchState.copy(zoomScale = 1.0)
        }
    }

    return TouchGestureHandlers(
        onTouchStart = handleTouchStart,
        onTouchMove = handleTouchMove,
        onTouchEnd = handleTouchEnd,
        state = touchState
    )
}

/**
 * Touch gesture event handlers to attach to elements.
 */
data class TouchGestureHandlers(
    val onTouchStart: (Event) -> Unit,
    val onTouchMove: (Event) -> Unit,
    val onTouchEnd: (Event) -> Unit,
    val state: TouchState
)

/**
 * Calculate distance between two points.
 */
private fun calculateDistance(x1: Double, y1: Double, x2: Double, y2: Double): Double {
    val dx = x2 - x1
    val dy = y2 - y1
    return sqrt(dx * dx + dy * dy)
}

/**
 * Platform-specific time function for touch events.
 */
private fun currentTimeMillisTouch(): Long = js("Date.now()").unsafeCast<Long>()

/**
 * Detect if the device supports touch.
 */
fun isTouchDevice(): Boolean {
    return js("('ontouchstart' in window) || (navigator.maxTouchPoints > 0)").unsafeCast<Boolean>()
}

/**
 * Detect if the viewport is mobile-sized.
 */
fun isMobileViewport(): Boolean {
    return window.innerWidth <= 768
}

/**
 * Detect if the viewport is tablet-sized.
 */
fun isTabletViewport(): Boolean {
    return window.innerWidth in 769..1024
}

/**
 * Responsive breakpoints.
 */
object Breakpoints {
    const val MOBILE = 768
    const val TABLET = 1024
    const val DESKTOP = 1280
}

/**
 * Hook for responsive viewport detection.
 */
@Composable
fun useResponsive(): ResponsiveState {
    var state by remember { mutableStateOf(calculateResponsiveState()) }

    DisposableEffect(Unit) {
        val handleResize: (Event) -> Unit = {
            state = calculateResponsiveState()
        }

        window.addEventListener("resize", handleResize)

        onDispose {
            window.removeEventListener("resize", handleResize)
        }
    }

    return state
}

private fun calculateResponsiveState(): ResponsiveState {
    val width = window.innerWidth
    return ResponsiveState(
        isMobile = width <= Breakpoints.MOBILE,
        isTablet = width in (Breakpoints.MOBILE + 1)..Breakpoints.TABLET,
        isDesktop = width > Breakpoints.TABLET,
        viewportWidth = width,
        viewportHeight = window.innerHeight,
        isTouch = isTouchDevice()
    )
}

/**
 * Responsive state for the current viewport.
 */
data class ResponsiveState(
    val isMobile: Boolean,
    val isTablet: Boolean,
    val isDesktop: Boolean,
    val viewportWidth: Int,
    val viewportHeight: Int,
    val isTouch: Boolean
)
